import { isTextNoteId } from '@/components/diagrams/FlowChart/utils/textNoteUtils';
import { compose } from '@/utils/reducerUtils';

import {
  applyAutoLayoutToState,
  applySectionBoundsToState,
  hasFullyInitializedNodes,
} from '../state';
import { EntityDiagramActionFn, InitializeNodePositionsAction } from '../types';

export const initializeNodePositionsAction: EntityDiagramActionFn<
  InitializeNodePositionsAction
> = (state, action, queueEffect) => {
  queueEffect({ type: 'completeInitializationEffect' });

  // Capture the measured container aspect (if provided) into state before
  // running layout — applySectionBoundsToState reads it to pick its stretch
  // target, as do subsequent REACTFLOW_NODE_MEASUREMENT_EVENT / AUTO_POSITION
  // dispatches that call applySectionBoundsToState again.
  const stateWithAspect =
    action.targetAspectRatio !== undefined
      ? { ...state, containerAspectRatio: action.targetAspectRatio }
      : state;

  // Check if we should apply auto layout
  const hasInitializedNodes = hasFullyInitializedNodes(stateWithAspect);

  // Also check if the number of tile nodes matches saved configurations.
  // Exclude text notes from the saved count — they live alongside tile
  // configurations but don't correspond to tile nodes, so including them
  // produces a spurious mismatch that triggers auto-layout on every open,
  // which shifts the notes by the centroid delta.
  const tileNodes = stateWithAspect.nodes.filter(
    (node) => node.type === 'tile'
  );
  const currentTileNodesCount = tileNodes.length;
  const savedTileConfigurationsCount =
    stateWithAspect.nodeConfigurations.filter(
      (cfg) => !isTextNoteId(cfg.nodeID)
    ).length;
  const nodeCountMismatch =
    savedTileConfigurationsCount > 0 &&
    currentTileNodesCount !== savedTileConfigurationsCount;

  // Apply auto layout if we don't have initialized nodes OR if there's a node count mismatch
  const shouldApplyAutoLayout = !hasInitializedNodes || nodeCountMismatch;

  if (!shouldApplyAutoLayout) {
    return stateWithAspect;
  }

  const { state: newState } = applyAutoLayoutToState(stateWithAspect);

  return compose(applySectionBoundsToState)(newState);
};
